import { isUserInfoReady } from '@/core/collections/contracts-api/user-info/user-info.helper';
import EMPLOYER_GUARD from '@/core/guards/employer.guard';
import PAYMENT_SYSTEM_GUARD, { getPaymentSystemOverviewDefaultRouteName } from '@/core/guards/payment-system.guard';
import store from '@/core/store';
import { PaymentSystemKey } from '@nxtpeople/nxt-styles';
import { RouteRecordRaw } from 'vue-router';

const integrationsRoutes: Array<RouteRecordRaw> = [
   {
      path: '',
      name: 'Dimona',
      component: () => import('@/modules/integrations/pages/DimonaOverview.vue'),
      meta: { titleKey: 'integrations.dimona.dimona', searchable: true },
      beforeEnter: [
         EMPLOYER_GUARD,
         (to, from, next) => {
            const permissions = store.getters['userInfo/permissions'];

            if (checkAccess('view', permissions?.dimona_requests)) return next();
            if (checkAccess('view', permissions?.unified_post_requests)) return next({ name: 'UnifiedPostRequestsOverview' });

            return next({ path: '/integrations/payment-system' });
         },
      ],
   },
   {
      path: 'unified-post-requests',
      name: 'UnifiedPostRequestsOverview',
      meta: { titleKey: 'integrations.unified-post.unified-post', searchable: true },
      component: () => import('@/modules/integrations/pages/UnifiedPostRequestsOverview.vue'),
      beforeEnter: [
         EMPLOYER_GUARD,
         (to, from, next) => {
            const permissions = store.getters['userInfo/permissions'];

            if (checkAccess('view', permissions?.unified_post_requests)) return next();
            if (checkAccess('view', permissions?.dimona_requests)) return next({ name: 'Dimona' });

            return next({ path: '/integrations/payment-system' });
         },
      ],
   },
   {
      path: 'payment-system',
      name: 'PaymentSystemOverview',
      component: () => import('@/modules/integrations/pages/PaymentSystemOverview.vue'),
      meta: { titleKey: 'payment-systems.payment-system', searchable: true },
      beforeEnter: async (to, from, next) => {
         const isPaymentSystemIndex = to.path.replace(/\/$/, '').endsWith('/integrations/payment-system');
         if (!isPaymentSystemIndex) return next();

         await isUserInfoReady();

         const defaultRouteName = getPaymentSystemOverviewDefaultRouteName();
         if (to.name !== defaultRouteName) return next({ name: defaultRouteName, replace: true });

         return next();
      },
      children: [
         {
            path: '',
            name: 'ProcessedContractsOverview',
            component: () => import('@/modules/integrations/components/payment-system/ProcessedContractsOverview.vue'),
            beforeEnter: [EMPLOYER_GUARD, PAYMENT_SYSTEM_GUARD([PaymentSystemKey.EasyPay, PaymentSystemKey.ETM])],
            meta: { titleKey: 'payment-systems.processed-contracts', searchable: true },
         },
         {
            path: 'files',
            name: 'FilesOverview',
            component: () => import('@/modules/integrations/components/payment-system/FilesOverview.vue'),
            beforeEnter: [EMPLOYER_GUARD, PAYMENT_SYSTEM_GUARD([PaymentSystemKey.EasyPay, PaymentSystemKey.ETM])],
            meta: { titleKey: 'generic.file', searchable: true },
         },
         {
            path: 'tax-discount-files',
            name: 'TaxDiscountFilesOverview',
            component: () => import('@/modules/integrations/components/payment-system/TaxDiscountFilesOverview.vue'),
            beforeEnter: [EMPLOYER_GUARD, PAYMENT_SYSTEM_GUARD(PaymentSystemKey.EasyPay)],
            meta: { titleKey: 'customers.teams-with-tax-discounts', searchable: true },
         },
         {
            path: 'remunerations',
            name: 'RemunerationsOverview',
            component: () => import('@/modules/integrations/components/payment-system/renumerations/RemunerationsOverview.vue'),
            beforeEnter: [EMPLOYER_GUARD, PAYMENT_SYSTEM_GUARD(PaymentSystemKey.ETM)],
            meta: { titleKey: 'payment-system.remunerations.remuneration', searchable: true },
         },
         {
            path: 'exceptions',
            name: 'ExceptionsOverview',
            component: () => import('@/modules/integrations/components/payment-system/exceptions/ExceptionsOverview.vue'),
            beforeEnter: [EMPLOYER_GUARD, PAYMENT_SYSTEM_GUARD(PaymentSystemKey.ETM)],
            meta: { titleKey: 'payment-system.exceptions.exception', searchable: true },
         },
      ],
   },
];

export default integrationsRoutes;
